import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import formula1Service from '../services/formula1Service'
import { Formula1Race as Formula1RaceType, Formula1Prediction } from '../types/formula1'
import toast from 'react-hot-toast'
import Formula1PredictionForm from '../components/Formula1PredictionForm'

const Formula1RacePage: React.FC = () => {
  const { roomId, raceId } = useParams<{ roomId: string; raceId: string }>()

  const [isLoading, setIsLoading] = useState(true)
  const [race, setRace] = useState<Formula1RaceType | null>(null)
  const [prediction, setPrediction] = useState<Formula1Prediction | null>(null)

  useEffect(() => {
    setRace(null)
    setPrediction(null)
    setIsLoading(true)

    if (raceId && roomId) {
      const loadData = async () => {
        await loadRaceData()
        await loadExistingPrediction()
      }
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, roomId])

  const loadRaceData = async () => {
    if (!raceId) return
    try {
      setIsLoading(true)
      const raceData = await formula1Service.getRace(raceId)
      if (raceData) {
        if (typeof raceData.drivers === 'string') {
          try {
            raceData.drivers = JSON.parse(raceData.drivers)
          } catch (e) {
            console.error('Error parsing drivers:', e)
            raceData.drivers = []
          }
        }
        if (typeof raceData.cars === 'string') {
          try {
            raceData.cars = JSON.parse(raceData.cars)
          } catch (e) {
            console.error('Error parsing cars:', e)
            raceData.cars = []
          }
        }
        if (!Array.isArray(raceData.drivers)) raceData.drivers = []
        if (!Array.isArray(raceData.cars)) raceData.cars = []
        setRace(raceData)
      }
    } catch (error) {
      console.error('Error loading race:', error)
      toast.error('Failed to load race data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadExistingPrediction = async () => {
    if (!roomId || !raceId) return
    try {
      const pred = await formula1Service.getPrediction(roomId, raceId)
      if (pred && pred.predictedPositions) {
        setPrediction(pred)
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error loading prediction:', error)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
      </div>
    )
  }

  if (!race) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Race not found</h2>
          <Link
            to={`/formula1/${roomId}`}
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
          >
            Back to Room
          </Link>
        </div>
      </div>
    )
  }

  const drivers = Array.isArray(race.drivers) ? race.drivers : []

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Link
            to={`/formula1/${roomId}`}
            className="text-muted-foreground hover:text-foreground transition-colors text-sm sm:text-base inline-flex items-center gap-1 py-1 touch-manipulation"
          >
            ← Back to Room
          </Link>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
            🏎️ Race {race.day}: {race.city}
            {race.country ? `, ${race.country}` : ''}
          </h1>
        </div>

        <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
            {race.raceDate && (
              <div>
                <p className="text-muted-foreground text-xs sm:text-sm mb-0.5">Race Date</p>
                <p className="text-foreground font-semibold text-sm sm:text-base">{new Date(race.raceDate).toLocaleDateString()}</p>
              </div>
            )}
            {race.raceTime && (
              <div>
                <p className="text-muted-foreground text-xs sm:text-sm mb-0.5">Race Time</p>
                <p className="text-foreground font-semibold text-sm sm:text-base">{race.raceTime}</p>
              </div>
            )}
            <div>
              <p className="text-muted-foreground text-xs sm:text-sm mb-0.5">Status</p>
              <span
                className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                  race.status === 'completed'
                    ? 'bg-success/20 text-success'
                    : race.status === 'in-progress'
                    ? 'bg-warning/20 text-warning'
                    : 'bg-primary/20 text-primary'
                }`}
              >
                {race.status || 'upcoming'}
              </span>
            </div>
            <div>
              <p className="text-muted-foreground text-xs sm:text-sm mb-0.5">Drivers</p>
              <p className="text-foreground font-semibold text-sm sm:text-base">{drivers.length} drivers</p>
            </div>
          </div>
        </div>

        <Formula1PredictionForm
          roomId={roomId!}
          raceId={raceId!}
          race={race}
          onSaved={loadExistingPrediction}
        />

        {race.status === 'completed' && race.actualResults && (
          <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <h2 className="font-display text-base sm:text-lg font-semibold text-foreground mb-4">Actual Results</h2>
            <div className="space-y-2">
              {Object.entries(race.actualResults)
                .sort(([, a], [, b]) => a - b)
                .slice(0, 10)
                .map(([driver, position]) => {
                  const correct = prediction?.predictedPositions?.[driver] === position
                  return (
                    <div key={driver} className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-muted/20 border border-border/50">
                      <span className="text-foreground font-semibold text-small sm:text-body">
                        {position}. {driver}
                      </span>
                      {correct && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-success/20 text-success text-xs font-semibold">✅ Correct</span>
                      )}
                    </div>
                  )
                })}
            </div>
            {prediction && (
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                <p className="text-muted-foreground text-small sm:text-body">Your Points</p>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                    (prediction.totalPoints || 0) === 0 ? 'bg-destructive/20 text-destructive' : 'bg-success/20 text-success'
                  }`}
                >
                  {prediction.totalPoints || 0} points
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Formula1RacePage
